/**
 * The dashboard's four counters (M23-T04's endpoint, consumed by T05).
 *
 * Each tile carries a **count and the timestamp of the oldest item waiting**,
 * from one query on the server, so a number and its "oldest" line cannot
 * disagree — the frame draws a tile reading `2` above the footer "No requests"
 * (M23 defect D7), which is only reachable when the two are sourced separately.
 *
 * The server returns a timestamp, never a formatted age: freezing "14d oldest
 * not yet secured" into the API would freeze the wording and the reader's
 * timezone with it.
 */
import { apiFetch } from './client'

export type DashboardTile = {
  count: number
  /** Null exactly when `count` is 0. */
  oldest_created_at: string | null
}

export type DashboardData = {
  appointment_requests: DashboardTile
  diagnostics_payment: DashboardTile
  medication_requests: DashboardTile
  /**
   * **Null for a `hospital_admin` — not zero.** They cannot open Enquiries or
   * Leads, so there is no such queue to report on, and a count they cannot
   * click through to is a permission leak in miniature.
   */
  enquiries_and_leads: DashboardTile | null
}

export function dashboardKey(hospitalId: string | null) {
  return ['admin', 'dashboard', hospitalId] as const
}

/** GET /api/v1/admin/dashboard — scoped like every other admin list. */
export async function fetchDashboard(
  token: string | null,
  hospitalId: string | null,
): Promise<DashboardData> {
  const qs = hospitalId ? `?hospital_id=${encodeURIComponent(hospitalId)}` : ''
  const body = await apiFetch<{ data: DashboardData }>(
    `/api/v1/admin/dashboard${qs}`,
    { token },
  )
  return body.data
}

/**
 * "14d", "4h", "18m" — the frame's compact age, from a timestamp.
 *
 * Deliberately coarse: the tile is a triage signal, not a clock. Anything under
 * a minute reads as "now" rather than "0m", which would look like a bug.
 */
export function compactAge(iso: string, now: number = Date.now()): string {
  const mins = Math.floor((now - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h`
  return `${Math.floor(hrs / 24)}d`
}
